/**
 * The Guide's table of contents.
 *
 * Structure lives here, prose lives in src/i18n/guide/{en,fr,ar}.json. A page is
 * a list of sections, each a heading key and a body key, so adding a page is a
 * data change plus three paragraphs rather than three more components.
 */

export interface GuideSection {
  /** Suffix under `guide.pages.<page>.` for the heading. */
  heading: string;
  /** Suffix for the body. */
  body: string;
  /** Optional "where to find it" line. */
  where?: string;
}

export interface GuidePage {
  key: string;
  sections: GuideSection[];
  /** Pages that render something beyond prose. */
  special?: 'start' | 'glossary';
}

export const GUIDE_PAGES: GuidePage[] = [
  { key: 'start', sections: [], special: 'start' },
  {
    key: 'what',
    sections: [
      { heading: 'chainTitle', body: 'chainBody' },
      { heading: 'ruleTitle', body: 'ruleBody' },
      { heading: 'decideTitle', body: 'decideBody' },
      { heading: 'offlineTitle', body: 'offlineBody' },
    ],
  },
  {
    key: 'club',
    sections: [
      { heading: 'teamsTitle', body: 'teamsBody' },
      { heading: 'seasonsTitle', body: 'seasonsBody' },
      { heading: 'phasesEditTitle', body: 'phasesEditBody' },
      { heading: 'phasesVsBlocksTitle', body: 'phasesVsBlocksBody' },
      { heading: 'configTitle', body: 'configBody' },
      { heading: 'testsTitle', body: 'testsBody', where: 'testsWhere' },
      { heading: 'brandingTitle', body: 'brandingBody', where: 'brandingWhere' },
    ],
  },
  {
    key: 'squad',
    sections: [
      { heading: 'importTitle', body: 'importBody' },
      { heading: 'positionsMatchTitle', body: 'positionsMatchBody' },
      { heading: 'positionsFixTitle', body: 'positionsFixBody' },
      { heading: 'datesTitle', body: 'datesBody' },
      { heading: 'customTitle', body: 'customBody' },
      { heading: 'rolesTitle', body: 'rolesBody' },
      { heading: 'bulkTitle', body: 'bulkBody' },
      { heading: 'removeTitle', body: 'removeBody' },
      { heading: 'positionsTitle', body: 'positionsBody', where: 'positionsWhere' },
      { heading: 'pitchTitle', body: 'pitchBody' },
      { heading: 'coversTitle', body: 'coversBody' },
    ],
  },
  {
    key: 'plan',
    sections: [
      { heading: 'structureTitle', body: 'structureBody' },
      { heading: 'blocksTitle', body: 'blocksBody' },
      { heading: 'modifyTitle', body: 'modifyBody' },
      { heading: 'blocksPrintTitle', body: 'blocksPrintBody' },
      { heading: 'mdTitle', body: 'mdBody' },
      { heading: 'objectiveTitle', body: 'objectiveBody' },
      { heading: 'shapeTitle', body: 'shapeBody' },
    ],
  },
  {
    key: 'session',
    sections: [
      { heading: 'deleteTitle', body: 'deleteBody' },
      { heading: 'objectiveTitle', body: 'objectiveBody' },
      { heading: 'libraryTitle', body: 'libraryBody' },
      { heading: 'loadTitle', body: 'loadBody' },
      { heading: 'overrideTitle', body: 'overrideBody' },
    ],
  },
  {
    key: 'ai',
    sections: [
      { heading: 'plannerTitle', body: 'plannerBody', where: 'plannerWhere' },
      { heading: 'whereTitle', body: 'whereBody' },
      { heading: 'askTitle', body: 'askBody' },
      { heading: 'readTitle', body: 'readBody' },
      { heading: 'rulesTitle', body: 'rulesBody' },
      { heading: 'creditsTitle', body: 'creditsBody', where: 'creditsWhere' },
      { heading: 'limitsTitle', body: 'limitsBody' },
    ],
  },
  {
    key: 'run',
    sections: [
      { heading: 'dashboardTitle', body: 'dashboardBody' },
      { heading: 'registerTitle', body: 'registerBody' },
      { heading: 'ratingTitle', body: 'ratingBody' },
      { heading: 'whyNoPlayerRpe', body: 'whyNoPlayerRpeBody' },
      { heading: 'flagsTitle', body: 'flagsBody' },
      { heading: 'registerTitle', body: 'registerBody' },
      { heading: 'registerSessionsTitle', body: 'registerSessionsBody' },
    ],
  },
  {
    key: 'match',
    sections: [
      { heading: 'prepTitle', body: 'prepBody' },
      { heading: 'duringTitle', body: 'duringBody' },
      { heading: 'callUpTitle', body: 'callUpBody' },
      { heading: 'minutesTitle', body: 'minutesBody' },
      { heading: 'matchDayTitle', body: 'matchDayBody' },
      { heading: 'analysisTitle', body: 'analysisBody' },
      { heading: 'problemTitle', body: 'problemBody' },
    ],
  },
  {
    key: 'development',
    sections: [
      { heading: 'evaluationsTitle', body: 'evaluationsBody' },
      { heading: 'testingTitle', body: 'testingBody' },
      { heading: 'depthTitle', body: 'depthBody' },
      { heading: 'trialsTitle', body: 'trialsBody' },
      { heading: 'trialsBulkTitle', body: 'trialsBulkBody' },
      { heading: 'gkTitle', body: 'gkBody' },
    ],
  },
  {
    key: 'reports',
    sections: [
      { heading: 'formsTitle', body: 'formsBody' },
      { heading: 'reportsTitle', body: 'reportsBody' },
      { heading: 'sensitiveTitle', body: 'sensitiveBody' },
      { heading: 'decisionsTitle', body: 'decisionsBody' },
      { heading: 'outcomeTitle', body: 'outcomeBody' },
    ],
  },
  { key: 'numbers', sections: [], special: 'glossary' },
  {
    key: 'languages',
    sections: [
      { heading: 'localesTitle', body: 'localesBody' },
      { heading: 'printTitle', body: 'printBody' },
      { heading: 'dataTitle', body: 'dataBody' },
      { heading: 'privacyTitle', body: 'privacyBody' },
    ],
  },
];

/** The ordered path a new coach walks, and where each step lives. */
export const START_STEPS = [
  { key: 'team', to: '/teams' },
  { key: 'season', to: '/seasons' },
  { key: 'squad', to: '/import' },
  { key: 'roles', to: '/squad' },
  { key: 'calendar', to: '/calendar' },
  { key: 'week', to: '/plan' },
  { key: 'session', to: '/sessions' },
  { key: 'match', to: '/matches' },
] as const;

export type StartStepKey = (typeof START_STEPS)[number]['key'];

/** Every term the glossary lists, in the order a coach meets them. */
export const GLOSSARY_TERMS = [
  'rpe',
  'session_load',
  'expected_load',
  'planned_actual',
  'provenance',
  'acute_chronic',
  'readiness',
  'work_rest',
  'density',
  'alactic',
  'glycolytic',
  'aerobic',
  'microcycle',
  'md_label',
  'objective_chain',
  'test_direction',
  'depth_cover',
  'senior_readiness',
] as const;
